import { ChevronDownIcon, ChevronRightIcon, SearchIcon } from '@treams/treams-icons';
import { Sidebar, Subheading, Skeleton } from '@treams/treams-ui';
import * as graphql from 'generated/graphql';
import _ from 'lodash';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import useViewer from 'packages/kakapo-ui-application/hooks/useViewer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

export function CompetenciesSidebar(props: { user: graphql.User }) {
	const viewer = useViewer();
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const { jobPositions } = props.user;

	const jobTitles = jobPositions.map((jobPosition) => jobPosition.jobTitle);
	const jobTitleIDs = jobTitles.map((jobTitle) => jobTitle.id);

	const query = graphql.useJobTitlesQuery({ variables: { first: 1000 } });

	const [isExplorerOpen, setIsExplorerOpen] = useState(false);

	const basePath =
		viewer!.id === props.user.id ? '/competencies' : `/users/${props.user.id}/competencies`;

	const hasExplorer = useFeature('2023.jobCompetencies.explorer');
	const hasSearchOnSkills = useFeature('2023.searchOnSkills');

	return (
		<Sidebar>
			{hasSearchOnSkills && (
				<>
					<Sidebar.Item
						icon={<SearchIcon />}
						small
						title={t('skills.explorer.search')}
						path={`${basePath}/search`}
						exact
					/>

					<Sidebar.Separator />
				</>
			)}
			<Subheading mb={2}>{t('skills.explorer.jobTitles.my')}</Subheading>
			<Sidebar.Item
				small
				title={t('skills.explorer.jobTitles.combined')}
				path={`${basePath}/all`}
				exact
			/>
			{jobTitles.map((jobTitle) => (
				<Sidebar.Item
					key={jobTitle.id}
					small
					title={localize(jobTitle.title).text}
					path={`${basePath}/${jobTitle.id}`}
					exact
				/>
			))}

			{hasExplorer && (
				<>
					<Sidebar.Spacing />
					<Sidebar.Item
						small
						title={t('skills.explorer.jobTitles.all')}
						onClick={() => {
							setIsExplorerOpen(!isExplorerOpen);
						}}
						icon={isExplorerOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
					/>

					{isExplorerOpen && (
						<>
							{_.sortBy(query.data?.jobTitles.edges, ({ node }) => {
								return localize(node.title).text;
							})
								.filter(({ node }) => !jobTitleIDs.includes(node.id))
								.map(({ node }) => (
									<Sidebar.Item
										key={node.id}
										small
										title={localize(node.title).text}
										path={`${basePath}/${node.id}`}
										exact
									>
										<Box flexGrow={1} />
									</Sidebar.Item>
								))}
						</>
					)}
				</>
			)}
		</Sidebar>
	);
}

CompetenciesSidebar.Skeleton = function CompetenciesSidebarSkeleton() {
	const { t } = useTranslation();

	return (
		<Sidebar>
			<Subheading mb={2}>{t('skills.explorer.jobTitles.my')}</Subheading>
			<Skeleton.Repeat count={3}>
				<Sidebar.Item.Skeleton />
			</Skeleton.Repeat>
		</Sidebar>
	);
};
